/** New-tag dialog: tenant (or system) + category + name, POST, then navigate to
 *  the created tag so its data can be filled in against the category schema.
 *
 *  An External ID field appears only for an externally identified category: there
 *  the id, not the name, is the tag's identity, so it has to be settable at the
 *  moment the row is created rather than patched in afterwards. */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as tagsApi from '@/api/tags';
import type { TagCategoryBrief } from '@/api/tags';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useSnackbar } from '@/components/Snackbar';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { TagCategoryPicker } from './TagCategoryPicker';

export function TagCreateDialog({
  open,
  onClose,
  pageTenants,
}: {
  open: boolean;
  onClose: () => void;
  /** The page's operators (D9) — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}) {
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [systemTag, setSystemTag] = useState(false);
  const [category, setCategory] = useState<TagCategoryBrief | null>(null);
  const [name, setName] = useState('');
  const [customId, setCustomId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    resetTenant();
    setSystemTag(false);
    setCategory(null);
    setName('');
    setCustomId('');
    setError(null);
  };

  // Page tenants: every open starts a fresh form on the page's tenant, so a
  // Cancelled pick (and the fields that hang on it) never carries over.
  useEffect(() => {
    if (open && pageTenants !== undefined) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const mutation = useMutation({
    mutationFn: (body: tagsApi.TagCreateInput) => tagsApi.create(body),
    onSuccess: (tag) => {
      snackbar.show('Tag created.', 'success');
      qc.invalidateQueries({ queryKey: ['tags'] });
      reset();
      onClose();
      navigate(`/tags/${tag.id}/data`);
    },
    onError: (e) => setError(e instanceof ConsoleApiError ? e.message : 'Create failed.'),
  });

  const tenantId = systemTag ? null : (tenant?.id ?? null);
  const canSubmit =
    (systemTag || tenant !== null) && category !== null && name.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New tag</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={<Switch checked={systemTag} onChange={(e) => { setSystemTag(e.target.checked); if (e.target.checked) setTenant(null); setCategory(null); }} />}
            label="System tag (no tenant)"
          />
          {!systemTag && (
            <TenantPicker value={tenant} featured={pageTenants} onChange={(t) => { setTenant(t); setCategory(null); }} label="Operator" placeholder="Select operator" />
          )}
          <TagCategoryPicker
            value={category}
            onChange={(c) => { setCategory(c); if (!c?.external_identity) setCustomId(''); }}
            tourOperatorId={tenantId}
          />
          <TextField label="Name" size="small" fullWidth required value={name}
            onChange={(e) => setName(e.target.value)} inputProps={{ maxLength: 1024 }} />
          {category?.external_identity && (
            <TextField
              label="External ID" size="small" fullWidth value={customId}
              onChange={(e) => setCustomId(e.target.value)} inputProps={{ maxLength: 128 }}
              helperText="This category identifies tags by their upstream id, so two tags may share a name. Leave empty to identify this one by name instead."
            />
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained" disabled={!canSubmit}
          onClick={() =>
            mutation.mutate({
              tour_operator: tenantId,
              category: category!.id,
              name: name.trim(),
              ...(category!.external_identity && customId.trim()
                ? { custom_id: customId.trim() }
                : {}),
            })
          }
        >
          {mutation.isPending ? 'Creating…' : 'Create tag'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
